import './CiroPage.scss'

import {CaretDownOutlined} from '@ant-design/icons'
import {Alert, DatePicker, Select} from 'antd'
import dayjs, {Dayjs} from 'dayjs'
import {RangeValue} from 'rc-picker/lib/interface'
import React, {useContext, useState} from 'react'
import {useNavigate} from 'react-router-dom'

import AppContext from "../../../../context/AppContext";
import {useOkulCiroQuery} from '../../../../gql/GeneratedGraphqlTypes'
import {getFormattedPrice} from '../../../../helpers/Formatting'
import Spinner from '../../../spinner/Spinner'

const {RangePicker} = DatePicker
const {Option} = Select

const CiroPage: React.FC = () => {
    const navigate = useNavigate()
    const {okul, ciro} = useContext(AppContext)
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null)
    const acilis = dayjs(okul?.okul_acilis_tarih ?? '2024-06-01')
    const bitis = dayjs()
    const [dateRange, setDateRange] = useState<RangeValue<Dayjs>>(
        acilis.isBefore(bitis) ? [acilis, bitis] : [bitis, acilis]
    )

    const {data, loading, error, refetch} = useOkulCiroQuery({
        variables: {
            sube_id: okul?.id ? okul.id : 94,
            bas_tarih: dateRange?.[0]?.format('YYYY-MM-DD') ?? '2024-06-01',
            bit_tarih: dateRange?.[1]?.format('YYYY-MM-DD') ?? '2025-06-01',
        },
    })

    if (loading) {
        return <Spinner showSpinner={loading} color="red"/>
    }

    if (error) {
        console.error(error)
        navigate('/')
        return null
    }

    const uyeKategorileri = [
        {id: null, isim: "Tümü"}, // 🔹 "Tümü" seçeneği eklendi
        ...(data?.okulCiro?.uyeKategorileri ?? [])
    ];

    const handleCategoryChange = (value: number | null) => {
        setSelectedCategory(value);
        refetch({
            sube_id: okul?.id,
            bas_tarih: dateRange?.[0]?.format('YYYY-MM-DD') ?? '2024-06-01',
            bit_tarih: dateRange?.[1]?.format('YYYY-MM-DD') ?? '2025-06-01',
            kategori_secimi: value ?? null,
        });
    };
    const handleDateChange = (values: RangeValue<Dayjs>) => {
        setDateRange(values)
        if (values && values[0] && values[1]) {
            refetch({
                sube_id: okul?.id,
                bas_tarih: values[0].format('YYYY-MM-DD'),
                bit_tarih: values[1].format('YYYY-MM-DD'),
                kategori_secimi: selectedCategory,
            })
        }
    }

    const transformedData = [
        {
            key: '1',
            status: 'Toplam',
            orderCount: data?.okulCiro?.toplamSiparisSayisi ?? 0,
            totalExcludingTax: `${getFormattedPrice(data?.okulCiro?.toplamKdvHaric?.toString() ?? '0')} TL`,
            totalTax: `${getFormattedPrice(data?.okulCiro?.toplamKdv?.toString() ?? '0')} TL`,
            totalIncludingTax: `${getFormattedPrice(data?.okulCiro?.toplamKdvDahil?.toString() ?? '0')} TL`,
        },
        {
            key: '2',
            status: 'İptal',
            orderCount: data?.okulCiro?.iptalSiparisSayisi ?? 0,
            totalExcludingTax: `${getFormattedPrice(data?.okulCiro?.iptalKdvHaric?.toString() ?? '0')} TL`,
            totalTax: `${getFormattedPrice(data?.okulCiro?.iptalKdv?.toString() ?? '0')} TL`,
            totalIncludingTax: `${getFormattedPrice(data?.okulCiro?.iptalKdvDahil?.toString() ?? '0')} TL`,
        },
        {
            key: '3',
            status: 'Tamamlanan',
            orderCount: data?.okulCiro?.tamamlananSiparisSayisi ?? 0,
            totalExcludingTax: `${getFormattedPrice(data?.okulCiro?.tamamlananKdvHaric?.toString() ?? '0')} TL`,
            totalTax: `${getFormattedPrice(data?.okulCiro?.tamamlananKdv?.toString() ?? '0')} TL`,
            totalIncludingTax: `${getFormattedPrice(data?.okulCiro?.tamamlananKdvDahil?.toString() ?? '0')} TL`,
        },
    ]

    return (
        <div className="ciro-page">
            <div className="okul-sepeti-admin-header">
                <h1>Ciro</h1>
            </div>
            {ciro ?
                <>
                    <Alert
                        type="warning"
                        showIcon
                        message="Sonradan yapılan iadeler cirodan düşülmemiştir."
                    ></Alert>
                    <div className="filter-content">
                        <div className="filter-div">
                            <span className="filter-title">Kategori </span>
                            <Select
                                suffixIcon={
                                    <>
                                        Değiştir <CaretDownOutlined/>{' '}
                                    </>
                                }
                                placeholder={'Kategori seçiniz...'}
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                            >
                                {uyeKategorileri.map((category) => (
                                    <Option key={category?.id} value={category?.id}>
                                        {category?.isim}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                        <div className="filter-div">
                            <span className="filter-title">Tarih Aralığı </span>
                            <RangePicker
                                allowClear
                                separator={false}
                                suffixIcon={
                                    <>
                                        Değiştir <CaretDownOutlined/>{' '}
                                    </>
                                }
                                value={dateRange}
                                onChange={handleDateChange}
                                format="YYYY-MM-DD"
                            />
                        </div>
                    </div>

                    <div className="ciro-data">
                        {transformedData.map((item) => (
                            <div
                                key={item.key}
                                className={`ciro-card-item ${
                                    item.status === 'İptal' ? 'cancel' : item.status === 'Tamamlanan' ? 'success' : ''
                                } `}
                            >
                                <div className="ciro-card-item-title">
                                      <span className="status-title">
                                        {item.status}
                                          <span className="down-title">Sipariş Sayısı</span>
                                      </span>

                                    <div className="order-count-title">{item.orderCount}</div>
                                </div>

                                <div className="card-content">
                                    <div className="content">
                                        <span className="content-title">KDV Hariç</span>
                                        <span>{item.totalExcludingTax}</span>
                                    </div>
                                    <div className="content">
                                        <span className="content-title">KDV Toplam</span>
                                        <span>{item.totalTax}</span>
                                    </div>
                                    <div className="content">
                                        <span className="content-title">KDV Dahil Toplam</span>
                                        <span>{item.totalIncludingTax}</span>
                                    </div>
                                </div>

                                {/*<div className="btn-ciro-div">*/}
                                {/*    <Button className="detail-btn-ciro" type="primary">*/}
                                {/*        Detaylı Listele*/}
                                {/*    </Button>*/}
                                {/*</div>*/}
                            </div>
                        ))}
                    </div>
                </>

                :
                <p>Ciro yetkiniz bulunmamaktadır. İlgili okul temsilcinizden talep edebilirsiniz.</p>
            }
        </div>
    )
}

export default CiroPage
